import type { Block, BlockDoc, BlockType, EditorConfig } from "./types";

export function newBlockId(): string {
  // 8 random hex chars; collisions are negligible for the size of any one doc.
  return (
    Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6)
  );
}

export function emptyDoc(): BlockDoc {
  return { version: 1, blocks: [] };
}

export function makeBlock<T = unknown>(type: string, data: T): Block<T> {
  return { id: newBlockId(), type, data };
}

export function ensureDoc(value: unknown): BlockDoc {
  // Some MariaDB drivers return JSON columns as strings (the column type is
  // LONGTEXT under the hood). Try once to parse strings before giving up —
  // otherwise valid docs round-trip into emptyDoc() and the editor reopens
  // empty after publish.
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return ensureDoc(parsed);
    } catch {
      return emptyDoc();
    }
  }
  if (
    value &&
    typeof value === "object" &&
    "version" in (value as Record<string, unknown>) &&
    Array.isArray((value as Record<string, unknown>).blocks)
  ) {
    return value as BlockDoc;
  }
  return emptyDoc();
}

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPE[c] ?? c);
}

export function toHtml(doc: BlockDoc, config: EditorConfig): string {
  const types = new Map(config.blockTypes.map((t) => [t.type, t]));
  return doc.blocks
    .map((b) => {
      const t = types.get(b.type);
      if (!t) return "";
      try {
        return t.toHtml(b.data);
      } catch {
        return "";
      }
    })
    .filter(Boolean)
    .join("\n");
}

export function toExcerpt(doc: BlockDoc, config: EditorConfig, max = 220): string {
  const types = new Map(config.blockTypes.map((t) => [t.type, t]));
  const parts: string[] = [];
  for (const b of doc.blocks) {
    const t = types.get(b.type);
    if (!t?.toText) continue;
    const txt = t.toText(b.data);
    if (txt) parts.push(txt);
    if (parts.join(" ").length > max) break;
  }
  const out = parts.join(" ").replace(/\s+/g, " ").trim();
  return out.length > max ? out.slice(0, max - 1) + "…" : out;
}

/** Replace the data on the block at `id` (immutable update). */
export function updateBlock<T>(doc: BlockDoc, id: string, data: T): BlockDoc {
  return {
    ...doc,
    blocks: doc.blocks.map((b) => (b.id === id ? { ...b, data: data as unknown } : b)),
  };
}

export function insertBlock(doc: BlockDoc, atIndex: number, type: BlockType): { doc: BlockDoc; id: string } {
  const block = makeBlock(type.type, type.defaultData());
  const next = doc.blocks.slice();
  next.splice(Math.max(0, Math.min(atIndex, next.length)), 0, block);
  return { doc: { ...doc, blocks: next }, id: block.id };
}

export function removeBlock(doc: BlockDoc, id: string): BlockDoc {
  return { ...doc, blocks: doc.blocks.filter((b) => b.id !== id) };
}

export function moveBlock(doc: BlockDoc, fromId: string, toId: string): BlockDoc {
  const from = doc.blocks.findIndex((b) => b.id === fromId);
  const to = doc.blocks.findIndex((b) => b.id === toId);
  if (from === -1 || to === -1 || from === to) return doc;
  const next = doc.blocks.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return { ...doc, blocks: next };
}

export function duplicateBlock(doc: BlockDoc, id: string): BlockDoc {
  const idx = doc.blocks.findIndex((b) => b.id === id);
  if (idx === -1) return doc;
  const original = doc.blocks[idx]!;
  const copy: Block = { ...original, id: newBlockId(), data: structuredClone(original.data) };
  const next = doc.blocks.slice();
  next.splice(idx + 1, 0, copy);
  return { ...doc, blocks: next };
}

/**
 * Side-insert: replace the block at `id` with a `row` container that holds
 * the original block plus a freshly-instantiated block of `newType`, ordered
 * left/right per `side`. If the block at `id` IS already a `row`, the new
 * block is appended (or prepended) as another cell instead of nesting a row
 * inside a row — keeps the layout flat and avoids unbounded recursion.
 *
 * Returns the modified doc plus the synthetic id of the newly-inserted block
 * so the caller can move focus to it.
 */
export function sideInsertBlock(
  doc: BlockDoc,
  id: string,
  newType: BlockType,
  side: "left" | "right",
  maxRowCells = 4,
): { doc: BlockDoc; id: string } {
  const idx = doc.blocks.findIndex((b) => b.id === id);
  if (idx === -1) return { doc, id };
  const original = doc.blocks[idx]!;
  const newBlock = makeBlock(newType.type, newType.defaultData());

  // Already a row? Add a cell, capped at maxRowCells.
  if (original.type === "row") {
    const rowData = original.data as { cells: Block[]; ratio?: string };
    if ((rowData.cells?.length ?? 0) >= maxRowCells) {
      return { doc, id };
    }
    const nextCells =
      side === "left"
        ? [newBlock, ...(rowData.cells ?? [])]
        : [...(rowData.cells ?? []), newBlock];
    const updated: Block = {
      ...original,
      data: { ...rowData, cells: nextCells },
    };
    const blocks = doc.blocks.slice();
    blocks[idx] = updated;
    return { doc: { ...doc, blocks }, id: newBlock.id };
  }

  // Otherwise wrap the original + new in a fresh row.
  const cells = side === "left" ? [newBlock, original] : [original, newBlock];
  const rowBlock: Block = makeBlock("row", { cells, ratio: undefined });
  const blocks = doc.blocks.slice();
  blocks[idx] = rowBlock;
  return { doc: { ...doc, blocks }, id: newBlock.id };
}
